import { SupportedCurrency, SUPPORTED_CURRENCIES, DEFAULT_CURRENCY } from './types';

// Re-export DEFAULT_CURRENCY for convenience
export { DEFAULT_CURRENCY };

/**
 * Format currency amount with proper locale and symbol
 * Always uses English numerals (0-9) regardless of locale
 */
export function formatCurrency(
  amount: number | null | undefined,
  currency: SupportedCurrency = DEFAULT_CURRENCY
): string {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return `${SUPPORTED_CURRENCIES[currency].symbol}0.00`;
  }

  const config = SUPPORTED_CURRENCIES[currency];
  
  try {
    // Use appropriate locale to ensure English numerals
    return new Intl.NumberFormat(config.locale, {
      style: 'currency',
      currency: config.code,
      minimumFractionDigits: config.decimalPlaces,
      maximumFractionDigits: config.decimalPlaces,
    }).format(amount);
  } catch (error) {
    // Fallback to manual formatting if Intl fails
    console.warn(`Currency formatting failed for ${currency}:`, error);
    return `${config.symbol}${amount.toFixed(config.decimalPlaces)}`;
  }
}

/**
 * Get currency symbol for a given currency code
 */
export function getCurrencySymbol(currency: SupportedCurrency = DEFAULT_CURRENCY): string {
  return SUPPORTED_CURRENCIES[currency].symbol;
}

/**
 * Get currency configuration
 */
export function getCurrencyConfig(currency: SupportedCurrency = DEFAULT_CURRENCY) {
  return SUPPORTED_CURRENCIES[currency];
}

/**
 * Check if a currency code is supported
 */
export function isSupportedCurrency(currency: string): currency is SupportedCurrency {
  return currency in SUPPORTED_CURRENCIES;
}

/**
 * Validate and normalize currency code
 */
export function normalizeCurrency(currency: string | null | undefined): SupportedCurrency {
  if (!currency || !isSupportedCurrency(currency)) {
    return DEFAULT_CURRENCY;
  }
  return currency;
}

/**
 * Format amount as simple number with currency symbol (no decimals for whole numbers)
 */
export function formatCurrencySimple(
  amount: number | null | undefined,
  currency: SupportedCurrency = DEFAULT_CURRENCY
): string {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return `${getCurrencySymbol(currency)}0`;
  }

  const symbol = getCurrencySymbol(currency);
  
  // Show decimals only if needed
  if (amount % 1 === 0) {
    return `${symbol}${amount.toLocaleString()}`;
  }
  
  return `${symbol}${amount.toFixed(2)}`;
}

/**
 * Get list of all supported currencies for dropdowns
 */
export function getSupportedCurrencyOptions() {
  return Object.values(SUPPORTED_CURRENCIES).map(config => ({
    value: config.code,
    label: `${config.name} (${config.symbol})`,
    symbol: config.symbol,
  }));
}